import { GridFilterModel, GridPaginationModel } from '@mui/x-data-grid';
import { useAppSelector, useEffect, useNavigate, useAppDispatch, useMemo, useCallback, useState } from 'hooks';
import { ViewMode, DataStatus, RoutePath, UserRole } from 'types/enums';
import { PatientTableItem } from 'types/types/components';
import { patientsActions, ECGActions } from 'utils/store';
import { INITIAL_PAGE_SIZE } from '../constants/constants';

type Response = {
  patientsFormattedData: PatientTableItem[];
  isDataLoading: boolean;
  onPaginationChangeHandler: (paginationModel: GridPaginationModel) => void;
  onFilterModelChangeHandler: (filterModel: GridFilterModel | null) => void;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
};

const usePatientsData = (): Response => {
  const { data: userData } = useAppSelector(({ user }) => user);
  const {
    data: patientsData,
    status,
    monitoringFacilityId,
    patientsViewMode,
  } = useAppSelector(({ patients }) => patients);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const isFacilityLoading = useAppSelector(({ facility }) => facility.status === DataStatus.LOADING);
  const { patientsEcgCapture, status: ECGStatus } = useAppSelector(({ ecg }) => ecg);
  const [filter, setFilter] = useState<GridFilterModel | null>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { caregiverId, role } = userData || {};
  const isDataLoading = status === DataStatus.LOADING || isFacilityLoading;

  const onPaginationChangeHandler = useCallback(
    (paginationModel: GridPaginationModel) => {
      const { page, pageSize } = paginationModel;
      const skip = page * pageSize;
      if (patientsViewMode === ViewMode.CAREGIVER) {
        return dispatch(patientsActions.getCaregiverPatientsData({ id: caregiverId, skip, limit: pageSize }));
      }
      if (patientsViewMode === ViewMode.ADMIN && monitoringFacilityId) {
        return dispatch(patientsActions.getPatientsByFacilityId({ id: monitoringFacilityId, skip, limit: pageSize }));
      }

      return navigate(RoutePath.PATIENT_DETAIL);
    },
    [patientsViewMode, navigate, dispatch, caregiverId, monitoringFacilityId],
  );

  useEffect(() => {
    if (patientsViewMode === ViewMode.ADMIN && monitoringFacilityId) {
      dispatch(patientsActions.getPatientsByFacilityId({ id: monitoringFacilityId, limit: pageSize }));
    }
    if (patientsViewMode === ViewMode.CAREGIVER && caregiverId) {
      dispatch(patientsActions.getCaregiverPatientsData({ id: caregiverId, limit: INITIAL_PAGE_SIZE }));
    }
  }, [caregiverId, dispatch, monitoringFacilityId, pageSize, patientsViewMode]);

  useEffect(() => {
    if (patientsData && patientsData.length) {
      dispatch(ECGActions.getPatientsECG({ data: patientsData }));
    }
  }, [dispatch, patientsData]);

  useEffect(() => {
    if (role === UserRole.PATIENT) {
      return navigate(RoutePath.PATIENT_DETAIL);
    }
  }, [navigate, role]);

  const patientsECGData = useMemo(() => {
    if (ECGStatus === DataStatus.LOADING) {
      return patientsData.map((patient) => ({ ...patient, assessment: 'Loading', waveform: 'Loading' }));
    }

    const updatePatients: PatientTableItem[] = patientsData.map((patient, index) => {
      if (!patientsEcgCapture) {
        return { ...patient, waveform: 'N/A' };
      }

      const patientECGData = patientsEcgCapture[index];
      if (!patientECGData || patientECGData.status === 'rejected') {
        return { ...patient, waveform: 'N/A' };
      }

      return {
        ...patient,
        assessment: patientECGData.value[0]?.assessment || 'N/A',
        waveform: patientECGData.value,
      };
    });

    return updatePatients;
  }, [patientsData, patientsEcgCapture, ECGStatus]);

  const patientsFormattedData = useMemo(() => {
    if (!filter || !filter.items.length) {
      return patientsECGData;
    }

    const [activeFilter] = filter.items;
    if (activeFilter.field !== 'waveform') {
      return patientsECGData;
    }

    return patientsECGData.map((patient) => {
      if (typeof patient.waveform === 'string') {
        return patient;
      }

      return {
        ...patient,
        waveform: patient.waveform.filter((waveform) => waveform.ecgLead === activeFilter.operator),
      };
    });
  }, [patientsECGData, filter]);

  const onFilterModelChangeHandler = useCallback((filterEvent: GridFilterModel) => {
    setFilter(filterEvent);
  }, []);

  return {
    patientsFormattedData,
    isDataLoading,
    onFilterModelChangeHandler,
    onPaginationChangeHandler,
    pageSize,
    setPageSize,
  };
};

export { usePatientsData };
